/**
 * RadioGroup — grid of toggle buttons wired to react-hook-form via Controller.
 * Used for income-range and turnover-range fields in the questionnaire.
 */
import { Controller, useForm } from 'react-hook-form';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';
import type { QuestionnaireInput } from '../schema';

interface RadioGroupProps {
  label:   string;
  /** Controlled form field name. Expanded as the questionnaire grows. */
  name:    'annual_income_range' | 'annual_turnover_range' | 'entity_structure' | 'other_customer_category';
  options: ReadonlyArray<{ value: string; label: string; description?: string }>;
  control: ReturnType<typeof useForm<QuestionnaireInput>>['control'];
  error?:  string;
}

export function RadioGroup({ label, name, options, control, error }: RadioGroupProps) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
        {label}
      </p>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {options.map((opt) => {
              const selected = field.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => field.onChange(opt.value)}
                  className="rounded-lg border-2 px-3 py-2.5 text-sm font-medium text-left transition-all focus:outline-none"
                  style={{
                    borderColor:     selected ? PALETTE_FOREST : 'hsl(var(--border))',
                    backgroundColor: selected ? PALETTE_MINT   : 'transparent',
                    color:           selected ? PALETTE_FOREST : 'hsl(var(--foreground))',
                  }}
                >
                  <span className="block leading-tight">{opt.label}</span>
                  {opt.description && (
                    <span
                      className="block text-[11px] mt-0.5 leading-snug"
                      style={{ color: selected ? PALETTE_FOREST : 'hsl(var(--muted-foreground))', opacity: 0.85 }}
                    >
                      {opt.description}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      />
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
